import { Button, Group, Space, Text } from '@mantine/core';
import { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useCookies } from 'react-cookie';
import { ReactFormBuilder } from 'bookitup-form-builder';
import { IntlProvider } from 'react-intl';
import { IconInfoCircle, IconPlus } from '@tabler/icons';
import { i18n } from '@lingui/core';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { BookitupFormContext } from '../../../../Service/Context/BookitupFormContext';
import FormBuilderLocale from '../lang/FormBuilderLocale';
import '../style/application.scss';
import ChangesDetectedAlert from '../ChangesDetectedAlert';
import RequiredFieldsAlert from '../RequiredFieldsAlert';
import BookitupAlert from '../../../../Atoms/BookitupAlert';
import { ThemeTitle } from '../../../../Atoms/ThemeText';
import { useEventKinds } from '../../../../Service/hooks/useEventKinds';
import { useBookitupTheme } from '../../../../Service/hooks/useBookitupTheme';
import CreateBasicCustomAttributeModal from '../CreateBasicCustomAttributeModal';
import { BASIC_CUSTOM_ATTRIBUTE_TYPES, createFormElementFromCustomAttribute } from '../utils/FormElementsStore';

const BASIC_ATTRIBUTE_TOOLBAR_ITEMS = {
  STRING: { label: 'Text', icon: 'fas fa-font' },
  SELECT: { label: 'Einfachauswahl', icon: 'far fa-caret-square-down' },
  TEXT: { label: 'Mehrzeiliger Text', icon: 'fas fa-text-height' },
  DATE: { label: 'Datum', icon: 'far fa-calendar-alt' },
  TIME: { label: 'Uhrzeit', icon: 'far fa-clock' },
  NUMBER: { label: 'Zahl', icon: 'fas fa-plus' },
  BOOLEAN: { label: 'Ja/Nein', icon: 'far fa-dot-circle' },
  MULTIPLE_CHOICE: { label: 'Mehrfachauswahl', icon: 'far fa-check-square' },
};

const BasicCustomAttributeToolbarItems = ({ target, onSelect }) => {
  const [opened, setOpened] = useState(false);

  if (!target) return null;

  return createPortal(
    <li>
      <div className="toolbar-group-item">
        <button
          className="btn btn-link btn-block text-left"
          type="button"
          onClick={() => setOpened((current) => !current)}
        >
          Neues Feld erstellen
        </button>
        <div className={`collapse${opened ? ' show' : ''}`}>
          <ul>
            {BASIC_CUSTOM_ATTRIBUTE_TYPES.map((attributeType) => (
              <li key={attributeType} onClick={() => onSelect(attributeType)}>
                <i className={BASIC_ATTRIBUTE_TOOLBAR_ITEMS[attributeType].icon} />
                {BASIC_ATTRIBUTE_TOOLBAR_ITEMS[attributeType].label}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </li>,
    target,
  );
};

BasicCustomAttributeToolbarItems.propTypes = {
  target: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
};

BasicCustomAttributeToolbarItems.defaultProps = {
  target: null,
};

const FormBuilder = ({ toolbarItems, toolbarItemsVersion, refreshToolbarItems }) => {
  const { bookitupForm, updateForm, addCustomItem } = useContext(BookitupFormContext);
  const [newAttributeType, setNewAttributeType] = useState(null);
  const [toolbarRoot, setToolbarRoot] = useState(null);
  const builderRoot = useRef(null);
  const [locale] = useCookies(['locale']);
  const navigate = useNavigate();
  const { kindNames } = useEventKinds();
  const [colorScheme] = useBookitupTheme();

  useEffect(() => {
    setToolbarRoot(builderRoot.current?.querySelector('.react-form-builder-toolbar > ul') ?? null);
  }, [toolbarItemsVersion]);

  if (!bookitupForm && kindNames.length === 0) {
    return null;
  }

  const { task_data, outOfSync } = bookitupForm;
  const usableLocale = locale.locale === 'en' ? 'en' : 'de';
  const currentAppLocale = FormBuilderLocale[usableLocale];

  const addCreatedAttributeToForm = async (attribute, groupName) => {
    const formElement = createFormElementFromCustomAttribute(attribute, groupName);
    const updatedForm = await updateForm({ task_data: [...task_data, formElement] });
    if (!updatedForm) throw new Error('Could not add the custom attribute to the form');
    await refreshToolbarItems();
  };

  return (
    <IntlProvider defaultLocale="en" locale={currentAppLocale.locale} messages={currentAppLocale.messages}>
      <div>
        <link rel="stylesheet" href="https://use.fontawesome.com/releases/v6.2.1/css/all.css" />
        <link
          rel="stylesheet"
          href="https://stackpath.bootstrapcdn.com/bootstrap/4.4.1/css/bootstrap.min.css"
          integrity="sha384-Vkoo8x4CGsO3+Hhxv8T/Q5PaXtkKtu6ug5TOeNV6gBiFeWPGFN9MuhOf23Q9Ifjh"
          crossOrigin="anonymous"
        />
      </div>

      <BookitupAlert title="Tipp: So fügst Du individuelle Felder hinzu" icon={<IconInfoCircle />}>
        <Text mb="sm">
          Wenn du weitere individuelle Felder hinzufügen möchtest, kannst Du Dir einfach unter{' '}
          <Text
            span
            weight="bolder"
            color="blue"
            variant="link"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/settings/customfields')}
          >
            eigene Felder
          </Text>{' '}
          individuelle Felder erstellen. Anschließend kannst Du diese unter "Verfügbare Felder" einfach dem
          Kontaktformular hinzufügen.
        </Text>
        <Text>Damit lassen sich z.B. auch Felder mit Checkboxen oder Dropdowns hinzufügen.</Text>
      </BookitupAlert>
      <Space h="xs" />

      {outOfSync && <ChangesDetectedAlert />}
      <RequiredFieldsAlert />
      <ThemeTitle order={3} mb="xs" mt="xs">
        {' '}
        Vorgefertigte Layouts
      </ThemeTitle>
      <Group
        p="sm"
        mb={45}
        sx={(theme) => ({
          border: `2px dashed ${theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[6]}`,
        })}
      >
        {[
          'DATUM_START_END',
          'DATE_AND_EVENT_KIND',
          'START_END',
          'FIRST_AND_LASTNAME',
          'SALUTATION_FIRST_AND_LAST_NAME',
          'PHONE_AND_EMAIL',
          'ZIP_AND_CITY',
          'PARTNER_FIRST_AND_LASTNAME',
          'PARTNER_SALUTATION_FIRST_AND_LAST_NAME',
          'PARTNER_PHONE_AND_EMAIL',
          'STREET_ZIP_AND_CITY',
          'TELEPHONE_EMAIL_DATE',
          'KIND_LOCATION_NAME_ORIGIN',
        ].map((customItem) => (
          <Button
            key={customItem}
            leftIcon={<IconPlus size={16} />}
            onClick={() => addCustomItem(customItem)}
            size="xs"
            variant="default"
          >
            {i18n._(`form.custom.field.${customItem}`)}
          </Button>
        ))}
      </Group>
      {toolbarItems && (
        <div ref={builderRoot} style={{ clear: 'both', display: 'flow-root' }}>
          <ReactFormBuilder
            key={toolbarItemsVersion}
            saveAlways
            toolbarItems={toolbarItems}
            data={task_data}
            onPost={(data) => updateForm({ task_data: data.task_data })}
            appLocaleOverride={FormBuilderLocale}
            locale={usableLocale}
            bookitupCtx={{
              kinds: kindNames,
              theme: colorScheme,
            }}
          />
          <BasicCustomAttributeToolbarItems target={toolbarRoot} onSelect={setNewAttributeType} />
        </div>
      )}
      <CreateBasicCustomAttributeModal
        attributeType={newAttributeType}
        onClose={() => setNewAttributeType(null)}
        onCreated={addCreatedAttributeToForm}
      />
    </IntlProvider>
  );
};

FormBuilder.propTypes = {
  toolbarItems: PropTypes.arrayOf(PropTypes.object),
  toolbarItemsVersion: PropTypes.number.isRequired,
  refreshToolbarItems: PropTypes.func.isRequired,
};

FormBuilder.defaultProps = {
  toolbarItems: undefined,
};

export default FormBuilder;
